import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { db, storage } from "../../../firebase/config";
import { collection, query, where, onSnapshot, addDoc, updateDoc, doc, orderBy, getDocs, Timestamp } from "firebase/firestore";
import { useAuth } from "../../../context/AuthContext";
import { useTheme } from "../../../context/ThemeContext";
import Header from "../../../components/Header";
import { registrarAccion } from "../../../utils/trazabilidad.js";
import { enviarRecordatorioTurno, enviarLinkPortal } from "../../../integrations/gmailCentralizado";
import { generarTokenPortal } from "../portal/GenerarPortal";

const DIAS = ['Domingo','Lunes','Martes','Miércoles','Jueves','Viernes','Sábado'];
const MESES = ['enero','febrero','marzo','abril','mayo','junio','julio','agosto','septiembre','octubre','noviembre','diciembre'];

const TIPOS_TURNO = [
  { value: 'consulta', label: 'Consulta', duracion: 45 },
  { value: 'control', label: 'Control', duracion: 30 },
  { value: 'estudio', label: 'Estudio diagnóstico', duracion: 45 },
  { value: 'cirugia', label: 'Cirugía', duracion: 60 },
];

const ESTUDIOS_DISPONIBLES = [
  'Campo Visual Computarizado',
  'OCT Mácula y Nervio Óptico',
  'Topografía Corneal',
  'Ecobiometría',
  'Microscopía Especular',
  'Estudio Integral de Ojo Seco',
  'Test de Schirmer',
  'OCT Paquimétrica',
  'Retinografía Digital',
  'Biometría Óptica (IOL Master / Lenstar)',
];

const ESTADOS = [
  { value: 'programado', label: 'Programado' },
  { value: 'confirmado', label: 'Confirmado' },
  { value: 'espera', label: 'En sala' },
  { value: 'consultorio', label: 'En consultorio' },
  { value: 'atendido', label: 'Atendido' },
  { value: 'ausente', label: 'Ausente' },
  { value: 'cancelado', label: 'Cancelado' },
];


const HORAS = [];
for (let h = 8; h <= 21; h++) {
  HORAS.push(`${h.toString().padStart(2,'0')}:00`);
  if (h < 21) HORAS.push(`${h.toString().padStart(2,'0')}:30`);
}

function getTipoInfo(tipo) {
  return TIPOS_TURNO.find(t => t.value === tipo) || TIPOS_TURNO[0];
}

function formatFecha(fecha) {
  return `${DIAS[fecha.getDay()]}, ${fecha.getDate()} de ${MESES[fecha.getMonth()]} ${fecha.getFullYear()}`;
}

function VistaSemanaMes({ turnos, fechaActual, vista, onDiaClick, theme }) {
  const DIAS_SEMANA = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
  const MESES_NOMBRES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

  function getLunes(fecha) {
    const d = new Date(fecha);
    const dia = d.getDay();
    const diff = dia === 0 ? -6 : 1 - dia;
    d.setDate(d.getDate() + diff);
    d.setHours(0,0,0,0);
    return d;
  }

  function getDiasSemana(fecha) {
    const lunes = getLunes(fecha);
    return Array.from({length: 7}, (_, i) => {
      const d = new Date(lunes);
      d.setDate(d.getDate() + i);
      return d;
    });
  }

  function getDiasMes(fecha) {
    const inicio = new Date(fecha.getFullYear(), fecha.getMonth(), 1);
    const fin = new Date(fecha.getFullYear(), fecha.getMonth() + 1, 0);
    const dias = [];
    const primerDia = inicio.getDay();
    const offset = primerDia === 0 ? 6 : primerDia - 1;
    for (let i = 0; i < offset; i++) dias.push(null);
    for (let d = new Date(inicio); d <= fin; d.setDate(d.getDate() + 1)) dias.push(new Date(d));
    return dias;
  }

  function turnosDelDia(fecha) {
    if (!fecha) return [];
    return turnos.filter(t => {
      const ft = t.fecha?.toDate?.();
      if (!ft) return false;
      return ft.getFullYear() === fecha.getFullYear() && ft.getMonth() === fecha.getMonth() && ft.getDate() === fecha.getDate();
    });
  }

  function isHoy(fecha) {
    if (!fecha) return false;
    const hoy = new Date();
    return fecha.getFullYear() === hoy.getFullYear() && fecha.getMonth() === hoy.getMonth() && fecha.getDate() === hoy.getDate();
  }

  const dias = vista === 'semana' ? getDiasSemana(fechaActual) : getDiasMes(fechaActual);

  return (
    <div style={{ padding: '0 24px' }}>
      {vista === 'mes' && (
        <div style={{ textAlign: 'center', fontSize: '15px', fontWeight: '600', color: theme.primary, marginBottom: '12px' }}>
          {MESES_NOMBRES[fechaActual.getMonth()]} {fechaActual.getFullYear()}
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px', marginBottom: '8px' }}>
        {DIAS_SEMANA.map(d => (
          <div key={d} style={{ textAlign: 'center', fontSize: '12px', fontWeight: '600', color: theme.secondary, padding: '4px' }}>{d}</div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px' }}>
        {dias.map((dia, i) => {
          const turnosDia = turnosDelDia(dia);
          const hoy = isHoy(dia);
          return (
            <div key={i} onClick={() => dia && onDiaClick(dia)} style={{
              minHeight: vista === 'mes' ? '80px' : '120px',
              background: hoy ? theme.infoBg : dia ? theme.bgCard : 'transparent',
              border: hoy ? `1.5px solid ${theme.accent}` : dia ? `0.5px solid ${theme.border}` : 'none',
              borderRadius: '8px', padding: '6px', cursor: dia ? 'pointer' : 'default', transition: 'border-color 0.15s',
            }}
              onMouseEnter={e => dia && (e.currentTarget.style.borderColor = theme.accent)}
              onMouseLeave={e => dia && !hoy && (e.currentTarget.style.borderColor = theme.border)}
            >
              {dia && (
                <>
                  <div style={{ fontSize: '13px', fontWeight: hoy ? '700' : '400', color: hoy ? theme.accent : theme.secondary, marginBottom: '4px' }}>
                    {dia.getDate()}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    {turnosDia.slice(0, vista === 'mes' ? 3 : 8).map(t => {
                      const hora = t.fecha?.toDate?.();
                      const horaStr = hora ? `${hora.getHours().toString().padStart(2,'0')}:${hora.getMinutes().toString().padStart(2,'0')}` : '';
                      return (
                        <div key={t.id} style={{ fontSize: '10px', padding: '2px 4px', borderRadius: '4px', background: theme.infoBg, color: theme.accent, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                          {horaStr} {t.paciente?.split(',')[0]}
                        </div>
                      );
                    })}
                    {turnosDia.length > (vista === 'mes' ? 3 : 8) && (
                      <div style={{ fontSize: '10px', color: theme.secondary, fontStyle: 'italic' }}>
                        +{turnosDia.length - (vista === 'mes' ? 3 : 8)} más
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Agenda() {
  const { currentUser, userRole, clinicaId, clinicaData } = useAuth();
  const MEDICOS = [
  { id: 'medico1', nombre: clinicaData?.medicos?.medico1 || 'Médico 1' },
  { id: 'medico2', nombre: clinicaData?.medicos?.medico2 || 'Médico 2' },
  { id: 'medico3', nombre: clinicaData?.medicos?.medico3 || 'Médico 3' },
  { id: 'medico4', nombre: clinicaData?.medicos?.medico4 || 'Médico 4' },
].filter(m => clinicaData?.medicos?.[m.id]);
  const { theme } = useTheme();
  const navigate = useNavigate();

  const btnPrimaryStyle = { background: `linear-gradient(135deg, ${theme.accent}, ${theme.accentDark})`, border: 'none', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer', color: '#FFFFFF', fontWeight: '600', fontFamily: "'Montserrat', sans-serif" };
  const btnSecondaryStyle = { background: 'transparent', border: `0.5px solid ${theme.borderInput}`, borderRadius: '8px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer', color: theme.secondary, fontFamily: "'Montserrat', sans-serif" };
  const btnStyle = { background: theme.bgCard, border: `0.5px solid ${theme.borderInput}`, borderRadius: '8px', padding: '6px 12px', color: theme.secondary, fontSize: '13px', cursor: 'pointer', fontFamily: "'Montserrat', sans-serif" };
  const selectStyle = { padding: '7px 12px', fontSize: '13px', borderRadius: '8px', border: `0.5px solid ${theme.borderInput}`, background: theme.bgInput, color: theme.primary, fontFamily: "'Montserrat', sans-serif", cursor: 'pointer', outline: 'none' };
  const inputStyle = { width: '100%', padding: '10px 14px', fontSize: '13px', border: `0.5px solid ${theme.borderInput}`, borderRadius: '8px', outline: 'none', color: theme.primary, background: theme.bgInput, boxSizing: 'border-box', fontFamily: "'Montserrat', sans-serif" };
  const labelStyle = { fontSize: '11px', color: theme.secondary, display: 'block', marginBottom: '6px', fontWeight: '500', letterSpacing: '1px' };
  const overlayStyle = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: theme.bgOverlay, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
  const modalStyle = { background: theme.bgModal, borderRadius: '16px', padding: '28px', width: '100%', maxWidth: '580px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.4)', border: `0.5px solid ${theme.border}` };

  const [fechaActual, setFechaActual] = useState(new Date());
  const [filtroMedico, setFiltroMedico] = useState(userRole === 'medico' ? 'medico1' : 'medico1');
  const [vistaAgenda, setVistaAgenda] = useState('dia');
  const [turnos, setTurnos] = useState([]);
  const [modalNuevo, setModalNuevo] = useState(false);
  const [modalDetalle, setModalDetalle] = useState(null);
  const [mostrarReprogramar, setMostrarReprogramar] = useState(false);
  const [nuevaFechaReprogramar, setNuevaFechaReprogramar] = useState('');
  const [nuevaHoraReprogramar, setNuevaHoraReprogramar] = useState('09:00');
  const [loading, setLoading] = useState(true);
  const [pacientesDB, setPacientesDB] = useState([]);
  const [sugerencias, setSugerencias] = useState([]);
  const [nuevoTurno, setNuevoTurno] = useState({
    paciente: '', dni: '', telefono: '', email: '', pacienteId: '',
    medico: 'medico1', tipo: 'consulta', hora: '09:00',
    cobertura: 'Particular', observaciones: '', detalle: '',
    indicacionesPre: '', indicacionesPost: '', receta: '', estudiosSeleccionados: []
  });

  const canEdit = ['gestion_general', 'recepcion'].includes(userRole);

  useEffect(() => {
    if (!clinicaId) return;
    const q = query(collection(db, 'pacientes'), where('clinicaId', '==', clinicaId), orderBy('apellido'));
    const unsub = onSnapshot(q, snap => setPacientesDB(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    return unsub;
  }, [clinicaId]);

  useEffect(() => {
    if (!clinicaId) return;
    let inicioRango = new Date(fechaActual);
    let finRango = new Date(fechaActual);
    if (vistaAgenda === 'dia') {
      inicioRango.setHours(0,0,0,0);
      finRango.setHours(23,59,59,999);
    } else if (vistaAgenda === 'semana') {
      const diaSemana = inicioRango.getDay();
      const diasHastaLunes = diaSemana === 0 ? -6 : 1 - diaSemana;
      inicioRango.setDate(inicioRango.getDate() + diasHastaLunes);
      inicioRango.setHours(0,0,0,0);
      finRango = new Date(inicioRango);
      finRango.setDate(finRango.getDate() + 6);
      finRango.setHours(23,59,59,999);
    } else if (vistaAgenda === 'mes') {
      inicioRango = new Date(fechaActual.getFullYear(), fechaActual.getMonth(), 1);
      inicioRango.setHours(0,0,0,0);
      finRango = new Date(fechaActual.getFullYear(), fechaActual.getMonth() + 1, 0);
      finRango.setHours(23,59,59,999);
    }
    const q = query(collection(db, 'turnos'), where('clinicaId', '==', clinicaId), where('fecha', '>=', inicioRango), where('fecha', '<=', finRango));
    const unsub = onSnapshot(q, snap => { setTurnos(snap.docs.map(d => ({ id: d.id, ...d.data() }))); setLoading(false); });
    return unsub;
  }, [fechaActual, vistaAgenda, clinicaId]);

  function cambiarDia(delta) {
    const nueva = new Date(fechaActual);
    if (vistaAgenda === 'semana') { nueva.setDate(nueva.getDate() + (delta > 0 ? 7 : -7)); }
    else if (vistaAgenda === 'mes') { nueva.setMonth(nueva.getMonth() + (delta > 0 ? 1 : -1)); }
    else {
      nueva.setDate(nueva.getDate() + delta);
      while (nueva.getDay() === 0 || nueva.getDay() === 6) nueva.setDate(nueva.getDate() + (delta > 0 ? 1 : -1));
    }
    setFechaActual(nueva);
  }

  function irHoy() {
    const hoy = new Date();
    if (hoy.getDay() === 0) hoy.setDate(hoy.getDate() + 1);
    if (hoy.getDay() === 6) hoy.setDate(hoy.getDate() + 2);
    setFechaActual(hoy);
  }

  async function crearTurno() {
    if (!nuevoTurno.paciente || !nuevoTurno.hora) return;
    const [h, m] = nuevoTurno.hora.split(':').map(Number);
    const fecha = new Date(fechaActual);
    fecha.setHours(h, m, 0, 0);
    const turnoDoc = await addDoc(collection(db, 'turnos'), { ...nuevoTurno, fecha, estado: 'programado', creadoEn: new Date(), clinicaId });
    await registrarAccion(currentUser?.email, userRole, 'Agenda', 'Crear turno', `${nuevoTurno.paciente} · ${nuevoTurno.tipo} · ${nuevoTurno.hora}`, clinicaId);
    if (nuevoTurno.dni) await generarTokenPortal(nuevoTurno.dni, nuevoTurno.paciente, nuevoTurno.email, fecha, clinicaId);
    setModalNuevo(false);
    setNuevoTurno({ paciente: '', dni: '', telefono: '', email: '', pacienteId: '', medico: 'medico1', tipo: 'consulta', hora: '09:00', cobertura: 'Particular', observaciones: '', detalle: '', indicacionesPre: '', indicacionesPost: '', receta: '', estudiosSeleccionados: [] });
  }

  async function cambiarEstado(turnoId, nuevoEstado) {
    const timestamps = {};
    if (nuevoEstado === 'espera') timestamps.timestampEspera = new Date();
    if (nuevoEstado === 'consultorio') timestamps.timestampConsultorio = new Date();
    if (nuevoEstado === 'atendido') timestamps.timestampAtendido = new Date();
    await updateDoc(doc(db, 'turnos', turnoId), { estado: nuevoEstado, ...timestamps });
    await registrarAccion(currentUser?.email, userRole, 'Agenda', 'Cambiar estado', `ID: ${turnoId} → ${nuevoEstado}`, clinicaId);
    if (modalDetalle?.id === turnoId) setModalDetalle(prev => ({ ...prev, estado: nuevoEstado, ...timestamps }));
  }

  const turnosFiltrados = turnos.filter(t => filtroMedico === 'todos' || t.medico === filtroMedico);
  const turnosMedico1 = turnosFiltrados.filter(t => t.medico === 'medico1').sort((a, b) => a.fecha?.toDate?.() - b.fecha?.toDate?.());
  const turnosMedico2 = turnosFiltrados.filter(t => t.medico === 'medico2').sort((a, b) => a.fecha?.toDate?.() - b.fecha?.toDate?.());

  const estadoBadge = {
    atendido: { bg: theme.successBg, color: theme.success },
    confirmado: { bg: theme.successBg, color: theme.success },
    espera: { bg: theme.warningBg, color: theme.warning },
    consultorio: { bg: theme.warningBg, color: theme.warning },
    programado: { bg: theme.bgCard, color: theme.secondary },
    ausente: { bg: theme.errorBg, color: theme.error },
    cancelado: { bg: theme.errorBg, color: theme.error },
  };

  const tipoBadge = {
    consulta: { bg: theme.infoBg, color: theme.accent },
    control: { bg: theme.successBg, color: theme.success },
    estudio: { bg: theme.warningBg, color: theme.warning },
    cirugia: { bg: theme.errorBg, color: theme.error },
  };

  function renderTurno(turno) {
    const tipo = getTipoInfo(turno.tipo);
    const hora = turno.fecha?.toDate?.() ? `${turno.fecha.toDate().getHours().toString().padStart(2,'0')}:${turno.fecha.toDate().getMinutes().toString().padStart(2,'0')}` : turno.hora;
    const eB = estadoBadge[turno.estado] || { bg: theme.bgCard, color: theme.secondary };
    const tB = tipoBadge[turno.tipo] || { bg: theme.bgCard, color: theme.secondary };
    return (
      <div key={turno.id} onClick={() => setModalDetalle(turno)} style={{ padding: '10px 12px', marginBottom: '6px', borderRadius: '10px', border: `0.5px solid ${theme.border}`, cursor: 'pointer', background: theme.bgCard, transition: 'border-color 0.15s' }}
        onMouseEnter={e => e.currentTarget.style.borderColor = theme.accent}
        onMouseLeave={e => e.currentTarget.style.borderColor = theme.border}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
          <span style={{ fontSize: '13px', fontWeight: '600', color: theme.primary }}>{hora} — {turno.paciente}</span>
          <span style={{ fontSize: '11px', padding: '2px 7px', borderRadius: '99px', fontWeight: '500', background: eB.bg, color: eB.color }}>
            {ESTADOS.find(e => e.value === turno.estado)?.label || turno.estado}
          </span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            <span style={{ fontSize: '11px', padding: '2px 7px', borderRadius: '99px', background: tB.bg, color: tB.color, fontWeight: '500' }}>{tipo.label}</span>
            <span style={{ fontSize: '12px', color: theme.secondary }}>{turno.cobertura}</span>
            {turno.detalle && <span style={{ fontSize: '11px', color: theme.secondary }}>{turno.detalle}</span>}
          </div>
          {['medico', 'gestion_general'].includes(userRole) && turno.estado !== 'atendido' && turno.estado !== 'cancelado' && turno.pacienteId && (
            <button onClick={e => { e.stopPropagation(); navigate(`/pacientes/${turno.pacienteId}/historia`); }} style={{ fontSize: '11px', padding: '3px 10px', borderRadius: '6px', background: theme.accent, color: '#FFFFFF', border: 'none', cursor: 'pointer', fontFamily: "'Montserrat', sans-serif", fontWeight: '600' }}>
              Atender
            </button>
          )}
        </div>
      </div>
    );
  }

  function renderColumna(medico, turnosList) {
    return (
      <div style={{ flex: 1, background: theme.bgCard, border: `0.5px solid ${theme.border}`, borderRadius: '12px', overflow: 'hidden', minWidth: 0 }}>
        <div style={{ padding: '10px 14px', borderBottom: `0.5px solid ${theme.border}`, background: theme.bg }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: theme.infoBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '600', color: theme.accent }}>{medico.initials}</div>
            <div>
              <div style={{ fontSize: '14px', fontWeight: '600', color: theme.primary }}>{medico.nombre}</div>
              <div style={{ fontSize: '12px', color: theme.secondary }}>{turnosList.length} turnos</div>
            </div>
          </div>
        </div>
        <div style={{ padding: '10px' }}>
          {turnosList.length === 0
            ? <div style={{ fontSize: '13px', color: theme.secondary, textAlign: 'center', padding: '24px 0' }}>Sin turnos para este día</div>
            : turnosList.map(renderTurno)
          }
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: theme.bg, fontFamily: "'Montserrat', sans-serif" }}>
      <Header titulo="Agenda" backLabel="Dashboard" backRuta="/dashboard" />
      <div style={{ padding: '24px', background: theme.bg }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button onClick={() => cambiarDia(-1)} style={btnStyle}>‹</button>
            <input type="date" value={fechaActual.toISOString().split('T')[0]} onChange={e => { const nueva = new Date(e.target.value + 'T12:00:00'); if (vistaAgenda === 'dia' && nueva.getDay() !== 0 && nueva.getDay() !== 6) setFechaActual(nueva); else if (vistaAgenda !== 'dia') setFechaActual(nueva); }} style={{ fontSize: '14px', fontWeight: '500', color: theme.primary, border: `0.5px solid ${theme.borderInput}`, borderRadius: '8px', padding: '6px 10px', fontFamily: "'Montserrat', sans-serif", background: theme.bgInput, cursor: 'pointer' }} />
            <button onClick={() => cambiarDia(1)} style={btnStyle}>›</button>
            <button onClick={irHoy} style={{ ...btnStyle, fontSize: '12px' }}>Hoy</button>
          </div>
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
            {['dia','semana','mes'].map(v => (
              <button key={v} onClick={() => setVistaAgenda(v)} style={{ padding: '6px 12px', fontSize: '12px', borderRadius: '8px', cursor: 'pointer', border: vistaAgenda === v ? 'none' : `0.5px solid ${theme.borderInput}`, background: vistaAgenda === v ? theme.accent : 'transparent', color: vistaAgenda === v ? '#FFFFFF' : theme.secondary, fontFamily: "'Montserrat', sans-serif", fontWeight: vistaAgenda === v ? '600' : '400' }}>
                {v === 'dia' ? 'Día' : v === 'semana' ? 'Semana' : 'Mes'}
              </button>
            ))}
            <select value={filtroMedico} onChange={e => setFiltroMedico(e.target.value)} style={selectStyle}>
              <option value="todos">Ver todos</option>
              {MEDICOS.map(m => (
                <option key={m.id} value={m.id}>{m.nombre}</option>
              ))}
            </select>
            {canEdit && <button onClick={() => setModalNuevo(true)} style={btnPrimaryStyle}>+ Nuevo turno</button>}
          </div>
        </div>

        {loading
          ? <div style={{ textAlign: 'center', color: theme.secondary, padding: '48px' }}>Cargando turnos...</div>
          : vistaAgenda === 'dia'
            ? <div style={{ display: 'flex', gap: '16px' }}>
                {MEDICOS.map(m => {
                  const turnosMedico = turnosFiltrados.filter(t => t.medico === m.id).sort((a, b) => a.fecha?.toDate?.() - b.fecha?.toDate?.());
                  if (filtroMedico !== 'todos' && filtroMedico !== m.id) return null;
                  return renderColumna({ ...m, initials: m.nombre?.[0] || m.id }, turnosMedico);
                })}
              </div>
            : vistaAgenda === 'semana'
              ? <VistaSemanaMes turnos={turnosFiltrados} fechaActual={fechaActual} vista="semana" theme={theme} onDiaClick={fecha => { setFechaActual(fecha); setVistaAgenda('dia'); }} />
              : <VistaSemanaMes turnos={turnosFiltrados} fechaActual={fechaActual} vista="mes" theme={theme} onDiaClick={fecha => { setFechaActual(fecha); setVistaAgenda('dia'); }} />
        }
      </div>

      {modalNuevo && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <span style={{ fontSize: '16px', fontWeight: '600', color: theme.primary }}>Nuevo turno</span>
              <button onClick={() => setModalNuevo(false)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: theme.secondary }}>×</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div style={{ gridColumn: '1 / -1', position: 'relative' }}>
                <label style={labelStyle}>PACIENTE *</label>
                <input value={nuevoTurno.paciente} onChange={e => { const valor = e.target.value; setNuevoTurno({...nuevoTurno, paciente: valor}); if (valor.length >= 2) { setSugerencias(pacientesDB.filter(p => `${p.apellido} ${p.nombre}`.toLowerCase().includes(valor.toLowerCase()) || p.dni?.includes(valor)).slice(0, 5)); } else setSugerencias([]); }} placeholder="Escribí apellido, nombre o DNI..." style={inputStyle} />
                {sugerencias.length > 0 && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, background: theme.bgModal, border: `0.5px solid ${theme.border}`, borderRadius: '8px', boxShadow: '0 4px 16px rgba(0,0,0,0.3)', overflow: 'hidden', marginTop: '2px' }}>
                    {sugerencias.map(p => (
                      <div key={p.id} onClick={() => { setNuevoTurno({...nuevoTurno, paciente: `${p.apellido}, ${p.nombre}`, dni: p.dni || '', telefono: p.telefono || '', email: p.email || '', cobertura: p.cobertura || 'Particular', pacienteId: p.id || ''}); setSugerencias([]); }} style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: `0.5px solid ${theme.border}` }}
                        onMouseEnter={e => e.currentTarget.style.background = theme.bgCard}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <div style={{ fontSize: '13px', fontWeight: '500', color: theme.primary }}>{p.apellido}, {p.nombre}</div>
                        <div style={{ fontSize: '11px', color: theme.secondary }}>DNI {p.dni} · {p.cobertura}</div>
                      </div>
                    ))}
                    <div onClick={() => setSugerencias([])} style={{ padding: '8px 14px', fontSize: '12px', color: theme.secondary, cursor: 'pointer', textAlign: 'center' }}>Continuar con nombre nuevo</div>
                  </div>
                )}
              </div>
              <div><label style={labelStyle}>DNI</label><input value={nuevoTurno.dni} onChange={e => setNuevoTurno({...nuevoTurno, dni: e.target.value})} style={inputStyle} /></div>
              <div><label style={labelStyle}>TELÉFONO</label><input value={nuevoTurno.telefono} onChange={e => setNuevoTurno({...nuevoTurno, telefono: e.target.value})} style={inputStyle} /></div>
              <div><label style={labelStyle}>EMAIL</label><input type="email" value={nuevoTurno.email} onChange={e => setNuevoTurno({...nuevoTurno, email: e.target.value})} style={inputStyle} /></div>
              <div><label style={labelStyle}>MÉDICO *</label>
                <select value={nuevoTurno.medico} onChange={e => setNuevoTurno({...nuevoTurno, medico: e.target.value})} style={inputStyle}>
                  {MEDICOS.map(m => (
                    <option key={m.id} value={m.id}>{m.nombre}</option>
                  ))}
                </select>
              </div>
              <div><label style={labelStyle}>TIPO *</label>
                <select value={nuevoTurno.tipo} onChange={e => setNuevoTurno({...nuevoTurno, tipo: e.target.value})} style={inputStyle}>
                  {TIPOS_TURNO.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div><label style={labelStyle}>HORA *</label>
                <select value={nuevoTurno.hora} onChange={e => setNuevoTurno({...nuevoTurno, hora: e.target.value})} style={inputStyle}>
                  {HORAS.map(h => <option key={h} value={h}>{h}</option>)}
                </select>
              </div>
              <div><label style={labelStyle}>COBERTURA</label><input value={nuevoTurno.cobertura} onChange={e => setNuevoTurno({...nuevoTurno, cobertura: e.target.value})} style={inputStyle} /></div>
              {nuevoTurno.tipo === 'cirugia' && (
                <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>PROCEDIMIENTO</label><input value={nuevoTurno.detalle} onChange={e => setNuevoTurno({...nuevoTurno, detalle: e.target.value})} style={inputStyle} /></div>
              )}
              {nuevoTurno.tipo === 'estudio' && (
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={labelStyle}>ESTUDIOS A REALIZAR</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '4px' }}>
                    {ESTUDIOS_DISPONIBLES.map(estudio => {
                      const sel = nuevoTurno.estudiosSeleccionados.includes(estudio);
                      return (
                        <button key={estudio} type="button" onClick={() => { const nuevos = sel ? nuevoTurno.estudiosSeleccionados.filter(e => e !== estudio) : [...nuevoTurno.estudiosSeleccionados, estudio]; setNuevoTurno({...nuevoTurno, estudiosSeleccionados: nuevos, detalle: nuevos.join(', ')}); }} style={{ fontSize: '12px', padding: '5px 10px', borderRadius: '99px', cursor: 'pointer', border: sel ? 'none' : `0.5px solid ${theme.borderInput}`, background: sel ? theme.accent : 'transparent', color: sel ? '#FFFFFF' : theme.secondary, fontFamily: "'Montserrat', sans-serif", fontWeight: sel ? '600' : '400' }}>
                          {estudio}
                        </button>
                      );
                    })}
                  </div>
                  {nuevoTurno.estudiosSeleccionados.length > 0 && <div style={{ fontSize: '11px', color: theme.secondary, marginTop: '6px' }}>Seleccionados: {nuevoTurno.estudiosSeleccionados.join(' · ')}</div>}
                  <div style={{ marginTop: '8px' }}>
                    <label style={labelStyle}>OTROS (especificá si no está en la lista)</label>
                    <input value={nuevoTurno.otrosEstudios || ''} onChange={e => setNuevoTurno({...nuevoTurno, otrosEstudios: e.target.value, detalle: [...nuevoTurno.estudiosSeleccionados, e.target.value].filter(Boolean).join(', ')})} placeholder="Ej: Electroretinograma..." style={inputStyle} />
                  </div>
                </div>
              )}
              <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>OBSERVACIONES</label><textarea value={nuevoTurno.observaciones} onChange={e => setNuevoTurno({...nuevoTurno, observaciones: e.target.value})} rows={2} style={{ ...inputStyle, resize: 'vertical' }} /></div>
              {['medico', 'gestion_general'].includes(userRole) && (
                <>
                  <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>INDICACIONES PRE-TURNO</label><textarea value={nuevoTurno.indicacionesPre} onChange={e => setNuevoTurno({...nuevoTurno, indicacionesPre: e.target.value})} rows={2} style={{ ...inputStyle, resize: 'vertical' }} /></div>
                  <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>INDICACIONES POST-TURNO</label><textarea value={nuevoTurno.indicacionesPost} onChange={e => setNuevoTurno({...nuevoTurno, indicacionesPost: e.target.value})} rows={2} style={{ ...inputStyle, resize: 'vertical' }} /></div>
                  <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>RECETA</label><textarea value={nuevoTurno.receta} onChange={e => setNuevoTurno({...nuevoTurno, receta: e.target.value})} rows={3} style={{ ...inputStyle, resize: 'vertical' }} /></div>
                </>
              )}
            </div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '20px' }}>
              <button onClick={crearTurno} style={btnPrimaryStyle}>Guardar turno</button>
              <button onClick={() => setModalNuevo(false)} style={btnSecondaryStyle}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {modalDetalle && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <span style={{ fontSize: '16px', fontWeight: '600', color: theme.primary }}>Detalle del turno</span>
              <button onClick={() => { setModalDetalle(null); setMostrarReprogramar(false); setNuevaFechaReprogramar(''); setNuevaHoraReprogramar('09:00'); }} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: theme.secondary }}>×</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px' }}>
              {[
                ['Paciente', modalDetalle.paciente],
                ['DNI', modalDetalle.dni || '—'],
                ['Teléfono', modalDetalle.telefono || '—'],
                ['Cobertura', modalDetalle.cobertura],
                ['Médico', MEDICOS.find(m => m.id === modalDetalle.medico)?.nombre || modalDetalle.medico],
                ['Tipo', getTipoInfo(modalDetalle.tipo).label],
                ['Hora', modalDetalle.fecha?.toDate?.() ? `${modalDetalle.fecha.toDate().getHours().toString().padStart(2,'0')}:${modalDetalle.fecha.toDate().getMinutes().toString().padStart(2,'0')}` : modalDetalle.hora],
              ].map(([label, value]) => (
                <div key={label} style={{ background: theme.bgCard, padding: '8px 10px', borderRadius: '8px', border: `0.5px solid ${theme.border}` }}>
                  <div style={{ fontSize: '11px', color: theme.secondary, marginBottom: '2px', fontWeight: '500' }}>{label}</div>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: theme.primary }}>{value}</div>
                </div>
              ))}
            </div>
            {modalDetalle.detalle && <div style={{ background: theme.warningBg, padding: '10px 12px', borderRadius: '8px', fontSize: '13px', color: theme.warning, marginBottom: '12px', fontWeight: '500' }}>{modalDetalle.detalle}</div>}
            {modalDetalle.observaciones && <div style={{ background: theme.bgCard, padding: '10px 12px', borderRadius: '8px', fontSize: '13px', color: theme.primary, marginBottom: '16px', border: `0.5px solid ${theme.border}` }}>{modalDetalle.observaciones}</div>}

            {['medico', 'gestion_general'].includes(userRole) && (
              <>
                <div style={{ fontSize: '12px', color: theme.accent, marginBottom: '8px', fontWeight: '600', letterSpacing: '1px' }}>INDICACIONES Y RECETA</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
                  <div><label style={labelStyle}>Indicaciones pre-turno</label><textarea defaultValue={modalDetalle.indicacionesPre || ''} id="indicacionesPre" rows={2} style={{ ...inputStyle, resize: 'vertical' }} /></div>
                  <div><label style={labelStyle}>Indicaciones post-turno</label><textarea defaultValue={modalDetalle.indicacionesPost || ''} id="indicacionesPost" rows={2} style={{ ...inputStyle, resize: 'vertical' }} /></div>
                  <div><label style={labelStyle}>Receta</label><textarea defaultValue={modalDetalle.receta || ''} id="recetaTurno" rows={3} style={{ ...inputStyle, resize: 'vertical' }} /></div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div>
                      <label style={labelStyle}>ARCHIVO DE RECETA (PDF o JPG — opcional)</label>
                      <input type="file" accept=".pdf,.jpg,.jpeg,.png" id="archivoReceta" style={{ fontSize: '13px', color: theme.secondary, fontFamily: "'Montserrat', sans-serif" }} />
                    </div>
                    <button onClick={async () => {
                      const pre = document.getElementById('indicacionesPre').value;
                      const post = document.getElementById('indicacionesPost').value;
                      const receta = document.getElementById('recetaTurno').value;
                      const archivoInput = document.getElementById('archivoReceta');
                      const archivo = archivoInput?.files?.[0];
                      let archivoRecetaData = modalDetalle.archivoReceta || null;
                      if (archivo) {
                        try {
                          const { ref, uploadBytes, getDownloadURL } = await import('firebase/storage');
                          const nombreLimpio = archivo.name.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9._-]/g, '');
                          const storageRef = ref(storage, `recetas/${clinicaId}/${Date.now()}_${nombreLimpio}`);
                          await uploadBytes(storageRef, archivo);
                          const url = await getDownloadURL(storageRef);
                          archivoRecetaData = { nombre: archivo.name, url, tipo: archivo.type };
                        } catch (e) { console.warn('Error al subir receta:', e); }
                      }
                      await updateDoc(doc(db, 'turnos', modalDetalle.id), { indicacionesPre: pre, indicacionesPost: post, receta, ...(archivoRecetaData ? { archivoReceta: archivoRecetaData } : {}) });
                      setModalDetalle(prev => ({ ...prev, indicacionesPre: pre, indicacionesPost: post, receta, ...(archivoRecetaData ? { archivoReceta: archivoRecetaData } : {}) }));
                    }} style={{ ...btnPrimaryStyle, alignSelf: 'flex-start' }}>
                      Guardar indicaciones
                    </button>
                    {modalDetalle.archivoReceta && (
                      <a href={modalDetalle.archivoReceta.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '12px', padding: '4px 10px', borderRadius: '6px', background: theme.infoBg, color: theme.accent, textDecoration: 'none', fontWeight: '500', display: 'inline-block' }}>
                        📎 {modalDetalle.archivoReceta.nombre}
                      </a>
                    )}
                  </div>
                </div>
              </>
            )}

            {canEdit && (
              <>
                <div style={{ fontSize: '12px', color: theme.accent, marginBottom: '8px', fontWeight: '600', letterSpacing: '1px' }}>ESTADO EN CLÍNICA</div>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '12px' }}>
                  {ESTADOS.filter(e => e.value !== 'cancelado').map(estado => (
                    <button key={estado.value} onClick={() => cambiarEstado(modalDetalle.id, estado.value)} style={{ padding: '7px 12px', fontSize: '12px', borderRadius: '8px', cursor: 'pointer', border: modalDetalle.estado === estado.value ? 'none' : `0.5px solid ${theme.borderInput}`, background: modalDetalle.estado === estado.value ? theme.accent : 'transparent', color: modalDetalle.estado === estado.value ? '#FFFFFF' : theme.secondary, fontWeight: modalDetalle.estado === estado.value ? '600' : '400', fontFamily: "'Montserrat', sans-serif" }}>
                      {estado.label}
                    </button>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '16px' }}>
                  <button onClick={() => setMostrarReprogramar(!mostrarReprogramar)} style={{ padding: '7px 12px', fontSize: '12px', borderRadius: '8px', cursor: 'pointer', border: `0.5px solid ${theme.accent}`, background: mostrarReprogramar ? theme.accent : 'transparent', color: mostrarReprogramar ? '#FFFFFF' : theme.accent, fontWeight: '500', fontFamily: "'Montserrat', sans-serif" }}>
                    Reprogramar
                  </button>
                </div>
                {mostrarReprogramar && (
                  <div style={{ background: theme.bgCard, padding: '12px', borderRadius: '8px', marginBottom: '16px', border: `0.5px solid ${theme.border}` }}>
                    <div style={{ fontSize: '12px', color: theme.secondary, marginBottom: '8px', fontWeight: '500', letterSpacing: '1px' }}>NUEVA FECHA Y HORA</div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
                      <div style={{ flex: 1, minWidth: '140px' }}><label style={labelStyle}>Fecha</label><input type="date" value={nuevaFechaReprogramar} onChange={e => setNuevaFechaReprogramar(e.target.value)} style={inputStyle} /></div>
                      <div style={{ flex: 1, minWidth: '120px' }}><label style={labelStyle}>Hora</label><select value={nuevaHoraReprogramar} onChange={e => setNuevaHoraReprogramar(e.target.value)} style={inputStyle}>{HORAS.map(h => <option key={h} value={h}>{h}</option>)}</select></div>
                    </div>
                    <button onClick={async () => {
                      if (!nuevaFechaReprogramar || !nuevaHoraReprogramar) return;
                      const [h, m] = nuevaHoraReprogramar.split(':').map(Number);
                      const nuevaFecha = new Date(nuevaFechaReprogramar + 'T12:00:00');
                      nuevaFecha.setHours(h, m, 0, 0);
                      await updateDoc(doc(db, 'turnos', modalDetalle.id), { fecha: nuevaFecha, estado: 'programado', reprogramado: true, fechaOriginal: modalDetalle.fecha });
                      await registrarAccion(currentUser?.email, userRole, 'Agenda', 'Reprogramar turno', `${modalDetalle.paciente} → ${nuevaFechaReprogramar} ${nuevaHoraReprogramar}`, clinicaId);
                      setMostrarReprogramar(false); setNuevaFechaReprogramar(''); setNuevaHoraReprogramar('09:00'); setModalDetalle(null);
                    }} style={btnPrimaryStyle}>Confirmar reprogramación</button>
                  </div>
                )}
              </>
            )}

            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
              <button onClick={() => { setModalDetalle(null); setMostrarReprogramar(false); setNuevaFechaReprogramar(''); setNuevaHoraReprogramar('09:00'); }} style={btnSecondaryStyle}>Cerrar</button>
              {canEdit && modalDetalle.email && (
                <button onClick={async () => {
                  try {
                    const fecha = modalDetalle.fecha?.toDate?.() ? modalDetalle.fecha.toDate().toLocaleDateString('es-AR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' }) : '—';
                    const hora = modalDetalle.fecha?.toDate?.() ? `${modalDetalle.fecha.toDate().getHours().toString().padStart(2,'0')}:${modalDetalle.fecha.toDate().getMinutes().toString().padStart(2,'0')}` : modalDetalle.hora;
                    console.log('clinicaData:', clinicaData);
                    await enviarRecordatorioTurno({ nombre: modalDetalle.paciente, email: modalDetalle.email, fecha, hora, medico: MEDICOS.find(m => m.id === modalDetalle.medico)?.nombre, tipo: modalDetalle.tipo, indicacionesPre: modalDetalle.indicacionesPre || '' }, clinicaData);
                    alert('Recordatorio enviado correctamente');
                  } catch (e) { alert('Error: ' + e.message); }
                }} style={btnSecondaryStyle}>Enviar recordatorio</button>
              )}
              {canEdit && modalDetalle.dni && modalDetalle.email && (
                <button onClick={async () => {
                  try {
                    const fecha = modalDetalle.fecha?.toDate ? modalDetalle.fecha.toDate() : new Date();
                    const nuevoToken = await generarTokenPortal(modalDetalle.dni, modalDetalle.paciente, modalDetalle.email, fecha, clinicaId);
                    if (!nuevoToken) { alert('No se pudo generar el token'); return; }
                    console.log('clinicaData:', clinicaData);
                    await enviarLinkPortal({ nombre: modalDetalle.paciente, email: modalDetalle.email }, nuevoToken, clinicaData);
                    alert('Link del portal enviado correctamente');
                  } catch (e) { alert('Error: ' + e.message); }
                }} style={btnSecondaryStyle}>Enviar portal</button>
              )}
              {canEdit && (
                <button onClick={() => cambiarEstado(modalDetalle.id, 'cancelado')} style={{ ...btnSecondaryStyle, color: theme.error, borderColor: theme.error }}>Cancelar turno</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}